import { createContext, useContext, useState, ReactNode } from "react";
import { usePublishImpersonationTarget } from "@/contexts/ImpersonationTargetContext";

interface ClienteImpersonationContextType {
  impersonatedClienteEmail: string | null;
  impersonatedClientePersonaId: number | null;
  impersonatedClienteName: string | null;
  setImpersonatedCliente: (email: string | null, personaId: number | null, name: string | null) => void;
  clearImpersonation: () => void;
  isImpersonating: boolean;
}

const ClienteImpersonationContext = createContext<ClienteImpersonationContextType>({
  impersonatedClienteEmail: null,
  impersonatedClientePersonaId: null,
  impersonatedClienteName: null,
  setImpersonatedCliente: () => {},
  clearImpersonation: () => {},
  isImpersonating: false,
});

export function ClienteImpersonationProvider({ children }: { children: ReactNode }) {
  const [clienteEmail, setClienteEmail] = useState<string | null>(null);
  const [clientePersonaId, setClientePersonaId] = useState<number | null>(null);
  const [clienteName, setClienteName] = useState<string | null>(null);

  const setImpersonatedCliente = (email: string | null, personaId: number | null, name: string | null) => {
    setClienteEmail(email);
    setClientePersonaId(personaId);
    setClienteName(name);
  };

  const clearImpersonation = () => {
    setClienteEmail(null);
    setClientePersonaId(null);
    setClienteName(null);
  };

  return (
    <ClienteImpersonationContext.Provider
      value={{
        impersonatedClienteEmail: clienteEmail,
        impersonatedClientePersonaId: clientePersonaId,
        impersonatedClienteName: clienteName,
        setImpersonatedCliente,
        clearImpersonation,
        isImpersonating: !!clienteEmail,
      }}
    >
      {children}
    </ClienteImpersonationContext.Provider>
  );
}

export function useClienteImpersonation() {
  return useContext(ClienteImpersonationContext);
}

/**
 * Publica el impersonado de este contexto al núcleo global (menús/rol efectivos y
 * solo lectura de toda la app). Se llama SOLO desde el layout del portal: el
 * provider vive montado en toda la app y conserva la selección, pero al salir del
 * portal el layout se desmonta, se despublica y la app deja de estar en solo
 * lectura; al volver se republica.
 */
export function usePublicarVerComoCliente(): void {
  const { impersonatedClienteEmail, impersonatedClientePersonaId, impersonatedClienteName } = useClienteImpersonation();
  usePublishImpersonationTarget(
    // Este contexto no guarda el rol del cliente: se publica sin `rolId`.
    impersonatedClienteEmail
      ? { email: impersonatedClienteEmail, personaId: impersonatedClientePersonaId, nombre: impersonatedClienteName, rolId: null }
      : null
  );
}
